import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import { 
  Briefcase,
  Building2,
  GraduationCap,
  Stethoscope,
  Home as HomeIcon,
  Camera,
  Scale,
  TrendingUp,
  ArrowRight,
  Check,
  Star,
  Users,
  Sparkles
} from 'lucide-react';

const ForProfessionals = () => {
  const navigate = useNavigate();

  const professions = [
    {
      icon: Briefcase,
      title: 'Sales & Business Development',
      description: 'Close more deals with instant contact sharing and lead tracking. Never miss a follow-up again.',
      benefits: ['Instant lead capture', 'CRM integration', 'Deal pipeline tracking', 'Team analytics']
    },
    {
      icon: HomeIcon,
      title: 'Real Estate Agents',
      description: 'Share property listings, schedule viewings, and capture buyer contacts at open houses.',
      benefits: ['Property link sharing', 'Open house lead capture', 'Virtual tour integration', 'Client management']
    },
    {
      icon: Scale,
      title: 'Consultants & Freelancers',
      description: 'Showcase your expertise, share your portfolio, and book consultations seamlessly.',
      benefits: ['Portfolio showcase', 'Calendar booking', 'Service packages', 'Client testimonials']
    },
    {
      icon: Building2,
      title: 'Corporate Executives',
      description: 'Make powerful first impressions with premium NFC cards that reflect your brand.',
      benefits: ['Premium card designs', 'Company branding', 'Team management', 'Enterprise analytics']
    },
    {
      icon: Stethoscope,
      title: 'Healthcare Professionals',
      description: 'Share credentials, book appointments, and maintain patient connections professionally.',
      benefits: ['Credential showcase', 'Appointment booking', 'Practice information', 'Referral tracking']
    },
    {
      icon: Camera,
      title: 'Creative Professionals',
      description: 'Display your portfolio, share your work, and connect with clients in style.',
      benefits: ['Visual portfolio', 'Social media links', 'Booking calendar', 'Custom themes']
    },
  ];

  const testimonials = [
    {
      quote: "Piper Card has transformed how I network at conferences. I've captured 3x more leads since switching.",
      name: "Rahul Sharma",
      role: "Sales Director, Tech Solutions",
      rating: 5
    },
    {
      quote: "As a real estate agent, sharing property links instantly has been a game-changer for my business.",
      name: "Priya Patel",
      role: "Senior Real Estate Agent",
      rating: 5
    },
    {
      quote: "The CRM integration helps me never miss a follow-up. My conversion rate has improved significantly.",
      name: "Amit Kumar",
      role: "Business Consultant",
      rating: 5
    }
  ];

  return (
    <Layout>
      <SEO 
        title="For Professionals | Piper Card - Digital Business Cards for Every Industry"
        description="Discover how Piper Card helps professionals in sales, real estate, consulting, healthcare, and creative industries network more effectively."
        keywords={['professional business cards', 'NFC cards for sales', 'real estate business cards', 'consultant networking']}
      />

      <div className="min-h-screen">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Users className="w-4 h-4" />
                  For Every Professional
                </div>
                <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                  Built for How
                  <br />
                  <span className="text-amber-400">Professionals Network</span>
                </h1>
                <p className="text-xl text-gray-400 mb-8">
                  Whether you're in sales, real estate, consulting, or any professional field, 
                  Piper Card adapts to your unique networking needs.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    size="lg" 
                    className="bg-amber-500 hover:bg-amber-600 rounded-full px-8"
                    onClick={() => navigate('/shop')}
                  >
                    Get Your Card
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-gray-600 text-white hover:bg-gray-800 rounded-full px-8"
                    onClick={() => navigate('/features')}
                  >
                    View All Features
                  </Button>
                </div>
              </div>
              <div className="hidden lg:block">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-3xl blur-3xl"></div>
                  <img 
                    src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=600&h=700&fit=crop" 
                    alt="Professional networking"
                    className="relative rounded-3xl shadow-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Professions Grid */}
        <div className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Tailored for Your Industry
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Piper Card works for professionals across all industries
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {professions.map((profession, index) => (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-amber-500 group-hover:scale-110 transition-all duration-300">
                      <profession.icon className="w-7 h-7 text-amber-600 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{profession.title}</h3>
                    <p className="text-gray-600 mb-4 text-sm leading-relaxed">{profession.description}</p>
                    <div className="space-y-2">
                      {profession.benefits.map((benefit, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                          <Check className="w-4 h-4 text-green-500" />
                          {benefit}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Testimonials */}
        <div className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Trusted by Professionals
              </h2>
              <p className="text-xl text-gray-600">
                See what our users have to say
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <Card key={index} className="border-0 bg-white shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex gap-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
                      ))}
                    </div>
                    <p className="text-gray-700 mb-6 italic">"{testimonial.quote}"</p>
                    <div>
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-gray-500">{testimonial.role}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="py-16 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
              <div>
                <p className="text-4xl lg:text-5xl font-bold text-amber-400 mb-2">10,000+</p>
                <p className="text-gray-400">Professionals Using Piper Card</p>
              </div>
              <div>
                <p className="text-4xl lg:text-5xl font-bold text-amber-400 mb-2">500K+</p>
                <p className="text-gray-400">Contacts Shared</p>
              </div>
              <div>
                <p className="text-4xl lg:text-5xl font-bold text-amber-400 mb-2">4.9/5</p>
                <p className="text-gray-400">Average Rating</p>
              </div>
              <div>
                <p className="text-4xl lg:text-5xl font-bold text-amber-400 mb-2">50+</p>
                <p className="text-gray-400">Industries Served</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Join Thousands of Professionals
            </h2>
            <p className="text-white/90 mb-8 text-lg">
              Start networking smarter today with Piper Card
            </p>
            <Button 
              size="lg" 
              className="bg-white text-amber-600 hover:bg-gray-100 rounded-full px-10 py-6 text-lg shadow-xl"
              onClick={() => navigate('/shop')}
            >
              Get Started Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ForProfessionals;
